// AGC: SupplyPriceHistory - Price tracking and history
// AGC: Shows current price, price trend, and historical prices for both purchases and sales

import React, { useEffect, useState, useMemo } from 'react'
import { 
  TrendingUp, 
  TrendingDown,
  Minus,
  DollarSign,
  Loader2,
  BarChart3,
  ShoppingCart,
  ArrowDownCircle,
  ArrowUpCircle,
  List,
} from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import { useInventory } from '../../hooks/useInventory'
import type { InventoryMovement } from '../../types/inventory'
import { format } from 'date-fns'
import { es, pt, enUS } from 'date-fns/locale'

// =============================================================================
// AGC: TYPES
// =============================================================================

export interface SupplyPriceHistoryProps {
  inventoryItemId?: string
  inputName: string
  unit: string
}

type PriceFilter = 'all' | 'purchase' | 'sale'

interface PriceEntry {
  date: string
  price: number
  quantity: number
  source: string
  type: 'purchase' | 'sale'
}

// =============================================================================
// AGC: MINI TREND CHART (Simple SVG)
// =============================================================================

interface MiniTrendChartProps {
  prices: number[]
  width?: number
  height?: number
  color?: string
}

const MiniTrendChart: React.FC<MiniTrendChartProps> = ({ 
  prices, 
  width = 80, 
  height = 32,
  color = '#3B82F6' 
}) => {
  if (prices.length < 2) return null
  
  const min = Math.min(...prices)
  const max = Math.max(...prices)
  const range = max - min || 1
  
  const points = prices.map((price, index) => {
    const x = (index / (prices.length - 1)) * width
    const y = height - ((price - min) / range) * (height - 4) - 2
    return `${x},${y}`
  }).join(' ')
  
  const trend = prices[prices.length - 1] - prices[0]
  const trendColor = trend > 0 ? '#EF4444' : trend < 0 ? '#22C55E' : color
  
  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline
        fill="none"
        stroke={trendColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />
      <circle
        cx={width}
        cy={height - ((prices[prices.length - 1] - min) / range) * (height - 4) - 2}
        r="3"
        fill={trendColor}
      />
    </svg>
  )
}

// =============================================================================
// AGC: PRICE STATS CARD (purchase-only stats for cost tracking)
// =============================================================================

interface PriceStatsProps {
  currentPrice: number | null
  avgPrice: number | null
  minPrice: number | null
  maxPrice: number | null
  priceChange: number | null
  unit: string
}

const PriceStats: React.FC<PriceStatsProps> = ({
  currentPrice,
  avgPrice,
  minPrice,
  maxPrice,
  priceChange,
  unit,
}) => {
  const { t } = useTranslation()
  
  const getTrendIcon = () => {
    if (priceChange === null || priceChange === 0) {
      return <Minus className="w-4 h-4 text-gray-400" />
    }
    if (priceChange > 0) {
      return <TrendingUp className="w-4 h-4 text-red-500" />
    }
    return <TrendingDown className="w-4 h-4 text-emerald-500" />
  }
  
  const getTrendColor = () => {
    if (priceChange === null || priceChange === 0) return 'text-gray-500'
    if (priceChange > 0) return 'text-red-500'
    return 'text-emerald-500'
  }
  
  return (
    <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            {t('supplies.prices.current') || 'Preço Atual (Compra)'}
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {currentPrice !== null ? (
              <>R$ {currentPrice.toFixed(2)}<span className="text-sm font-normal text-gray-400">/{unit}</span></>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </p>
        </div>
        
        {priceChange !== null && priceChange !== 0 && (
          <div className={`flex items-center gap-1 ${getTrendColor()}`}>
            {getTrendIcon()}
            <span className="text-sm font-medium">
              {priceChange > 0 ? '+' : ''}{priceChange.toFixed(1)}%
            </span>
          </div>
        )}
      </div>
      
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
          <p className="text-xs text-gray-400 mb-1">
            {t('supplies.prices.average') || 'Médio'}
          </p>
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">
            {avgPrice !== null ? `R$ ${avgPrice.toFixed(2)}` : '—'}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
          <p className="text-xs text-gray-400 mb-1">
            {t('supplies.prices.min') || 'Mínimo'}
          </p>
          <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
            {minPrice !== null ? `R$ ${minPrice.toFixed(2)}` : '—'}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
          <p className="text-xs text-gray-400 mb-1">
            {t('supplies.prices.max') || 'Máximo'}
          </p>
          <p className="text-sm font-semibold text-red-600 dark:text-red-400">
            {maxPrice !== null ? `R$ ${maxPrice.toFixed(2)}` : '—'}
          </p>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// AGC: PRICE HISTORY ITEM — supports both purchase and sale entries
// =============================================================================

interface PriceHistoryItemProps {
  entry: PriceEntry
  unit: string
  locale: Locale
  isLatest: boolean
}

const PriceHistoryItem: React.FC<PriceHistoryItemProps> = ({ 
  entry, 
  unit, 
  locale,
  isLatest 
}) => {
  const { t } = useTranslation()
  const dateFormatted = format(new Date(entry.date), 'dd/MM/yyyy', { locale })
  
  const isPurchase = entry.type === 'purchase'
  
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg ${
      isLatest && isPurchase ? 'bg-blue-50 dark:bg-blue-900/20' : ''
    }`}>
      <div className="flex items-center gap-3">
        {/* AGC: Icon changes color/shape based on purchase vs sale */}
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
          isPurchase
            ? isLatest
              ? 'bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-400'
              : 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
            : 'bg-orange-50 dark:bg-orange-900/30 text-orange-500 dark:text-orange-400'
        }`}>
          {isPurchase
            ? <ArrowDownCircle className="w-4 h-4" />
            : <ArrowUpCircle className="w-4 h-4" />
          }
        </div>
        <div>
          <p className={`text-sm font-medium ${
            isPurchase
              ? isLatest
                ? 'text-blue-700 dark:text-blue-300'
                : 'text-gray-700 dark:text-gray-300'
              : 'text-orange-600 dark:text-orange-400'
          }`}>
            {entry.price > 0
              ? `R$ ${entry.price.toFixed(2)}/${unit}`
              : <span className="text-gray-400 dark:text-gray-500 italic text-xs">sem preço registrado</span>
            }
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {dateFormatted} • {entry.quantity.toFixed(1)} {unit}
          </p>
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        {/* AGC: Type badge */}
        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
          isPurchase
            ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
            : 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400'
        }`}>
          {isPurchase
            ? (t('supplies.prices.purchase') || 'Compra')
            : (t('supplies.prices.sale') || 'Venda')
          }
        </span>
        {/* AGC: Latest badge for most recent purchase */}
        {isLatest && isPurchase && (
          <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300 text-xs font-medium rounded-full">
            {t('supplies.prices.latest') || 'Último'}
          </span>
        )}
      </div>
    </div>
  )
}

// =============================================================================
// AGC: FILTER TOGGLE BUTTONS
// =============================================================================

interface FilterButtonsProps {
  activeFilter: PriceFilter
  onFilterChange: (filter: PriceFilter) => void
  counts: { all: number; purchase: number; sale: number }
}

const FilterButtons: React.FC<FilterButtonsProps> = ({ activeFilter, onFilterChange, counts }) => {
  const { t } = useTranslation()
  
  const buttons: { key: PriceFilter; label: string; icon: React.ReactNode }[] = [
    {
      key: 'all',
      label: t('supplies.prices.filterAll') || 'Todos',
      icon: <List className="w-3.5 h-3.5" />,
    },
    {
      key: 'purchase',
      label: t('supplies.prices.filterPurchases') || 'Compras',
      icon: <ArrowDownCircle className="w-3.5 h-3.5" />,
    },
    {
      key: 'sale',
      label: t('supplies.prices.filterSales') || 'Vendas',
      icon: <ArrowUpCircle className="w-3.5 h-3.5" />,
    },
  ]
  
  return (
    <div className="flex gap-1.5 flex-wrap">
      {buttons.map(btn => (
        <button
          key={btn.key}
          onClick={() => onFilterChange(btn.key)}
          className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
            activeFilter === btn.key
              ? btn.key === 'purchase'
                ? 'bg-emerald-600 text-white'
                : btn.key === 'sale'
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-700 dark:bg-gray-200 text-white dark:text-gray-800'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
          }`}
        >
          {btn.icon}
          {btn.label}
          <span className="ml-0.5 opacity-70">({counts[btn.key]})</span>
        </button>
      ))}
    </div>
  )
}

// =============================================================================
// AGC: MAIN COMPONENT
// =============================================================================

export const SupplyPriceHistory: React.FC<SupplyPriceHistoryProps> = ({
  inventoryItemId,
  inputName,
  unit,
}) => {
  const { t, language } = useTranslation()
  const { getMovementsForItem } = useInventory()
  
  const [movements, setMovements] = useState<InventoryMovement[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeFilter, setActiveFilter] = useState<PriceFilter>('all')
  
  const getLocale = (): Locale => {
    switch (language) {
      case 'es': return es
      case 'pt': return pt
      default: return enUS
    }
  }
  
  useEffect(() => {
    const fetchMovements = async () => {
      if (!inventoryItemId) {
        setMovements([])
        setLoading(false)
        return
      }
      
      setLoading(true)
      setError(null)
      
      try {
        if (getMovementsForItem) {
          const data = await getMovementsForItem(inventoryItemId)
          setMovements(Array.isArray(data) ? data : [])
        }
      } catch (err) {
        console.error('AGC: Error fetching movements for price history:', err)
        setError(t('supplies.prices.fetchError') || 'Erro ao carregar preços')
      } finally {
        setLoading(false)
      }
    }
    
    fetchMovements()
  }, [inventoryItemId, getMovementsForItem, t])
  
  // AGC: Extract all price-bearing movements: both entries (purchases) and exits with sale price (sales)
  const allPriceHistory = useMemo<PriceEntry[]>(() => {
    if (!Array.isArray(movements)) return []
    
    const entries: PriceEntry[] = movements
      .filter(m => m.type === 'entry' && m.costPerUnit && m.costPerUnit > 0)
      .map(m => ({
        date: m.createdAt,
        price: m.costPerUnit!,
        quantity: m.quantity,
        source: m.source,
        type: 'purchase' as const,
      }))
    
    // AGC: Include ALL exit movements (sales) — show price if salePrice stored, else 0 means "no price"
    const exits: PriceEntry[] = movements
      .filter(m => m.type === 'exit')
      .map(m => ({
        date: m.createdAt,
        // AGC: Prefer explicit salePrice; fall back to null (shown as "—" in UI)
        price: m.salePrice ?? 0,
        quantity: Math.abs(m.quantity),
        source: m.source,
        type: 'sale' as const,
      }))
    
    return [...entries, ...exits].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    )
  }, [movements])
  
  // AGC: Filter counts for badge display
  const filterCounts = useMemo(() => ({
    all: allPriceHistory.length,
    purchase: allPriceHistory.filter(e => e.type === 'purchase').length,
    sale: allPriceHistory.filter(e => e.type === 'sale').length,
  }), [allPriceHistory])
  
  // AGC: Filtered list based on active filter
  const filteredHistory = useMemo<PriceEntry[]>(() => {
    if (activeFilter === 'all') return allPriceHistory
    return allPriceHistory.filter(e => e.type === activeFilter)
  }, [allPriceHistory, activeFilter])
  
  // AGC: Price stats always use purchases only (for cost tracking)
  const purchaseHistory = useMemo(
    () => allPriceHistory.filter(e => e.type === 'purchase'),
    [allPriceHistory]
  )
  
  const priceStats = useMemo(() => {
    if (purchaseHistory.length === 0) {
      return {
        currentPrice: null,
        avgPrice: null,
        minPrice: null,
        maxPrice: null,
        priceChange: null,
        trendPrices: [] as number[],
      }
    }
    
    const prices = purchaseHistory.map(p => p.price)
    const currentPrice = prices[0]
    const avgPrice = prices.reduce((sum, p) => sum + p, 0) / prices.length
    const minPrice = Math.min(...prices)
    const maxPrice = Math.max(...prices)
    
    let priceChange: number | null = null
    if (prices.length >= 2) {
      const oldPrices = prices.slice(1, 4)
      const oldAvg = oldPrices.reduce((sum, p) => sum + p, 0) / oldPrices.length
      priceChange = ((currentPrice - oldAvg) / oldAvg) * 100
    }
    
    const trendPrices = purchaseHistory
      .slice(0, 7)
      .map(p => p.price)
      .reverse()
    
    return { currentPrice, avgPrice, minPrice, maxPrice, priceChange, trendPrices }
  }, [purchaseHistory])
  
  const locale = getLocale()
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    )
  }
  
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-3">
          <DollarSign className="w-6 h-6 text-red-500" />
        </div>
        <p className="text-gray-500 dark:text-gray-400 text-sm">{error}</p>
      </div>
    )
  }
  
  if (!inventoryItemId) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
          <DollarSign className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-gray-600 dark:text-gray-300 font-medium mb-1">
          {t('supplies.prices.noPriceData') || 'Sem dados de preço'}
        </h3>
        <p className="text-gray-400 text-sm max-w-[280px]">
          {t('supplies.prices.noPriceDataDesc') || 
            'Quando adicionar estoque com preço, o histórico aparecerá aqui.'}
        </p>
      </div>
    )
  }
  
  if (allPriceHistory.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
          <BarChart3 className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-gray-600 dark:text-gray-300 font-medium mb-1">
          {t('supplies.prices.empty') || 'Sem histórico de preços'}
        </h3>
        <p className="text-gray-400 text-sm max-w-[280px]">
          {t('supplies.prices.emptyDesc') || 
            'Ao adicionar ou vender estoque com preço, o histórico aparecerá aqui.'}
        </p>
      </div>
    )
  }
  
  return (
    <div className="p-4">
      {/* AGC: Purchase cost stats at top — always based on purchase prices */}
      {purchaseHistory.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              {t('supplies.prices.statistics') || 'Estatísticas de Custo'}
            </h3>
            {priceStats.trendPrices.length >= 2 && (
              <MiniTrendChart prices={priceStats.trendPrices} />
            )}
          </div>
          
          <PriceStats 
            currentPrice={priceStats.currentPrice}
            avgPrice={priceStats.avgPrice}
            minPrice={priceStats.minPrice}
            maxPrice={priceStats.maxPrice}
            priceChange={priceStats.priceChange}
            unit={unit}
          />
        </div>
      )}
      
      {/* AGC: History header + filter buttons */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            {t('supplies.prices.history') || 'Histórico de Preços'} ({filteredHistory.length})
          </h3>
        </div>
        
        <FilterButtons
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
          counts={filterCounts}
        />
      </div>
      
      {/* AGC: Price history list */}
      <div className="space-y-2">
        {filteredHistory.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-gray-400 dark:text-gray-500 text-sm">
              {activeFilter === 'purchase'
                ? (t('supplies.prices.noPurchases') || 'Nenhuma compra registrada')
                : (t('supplies.prices.noSales') || 'Nenhuma venda registrada')}
            </p>
          </div>
        ) : (
          filteredHistory.map((entry, index) => {
            // AGC: "Latest" badge only on the first purchase entry overall (for cost reference)
            const isFirstPurchase = entry.type === 'purchase' && index === filteredHistory.findIndex(e => e.type === 'purchase')
            return (
              <PriceHistoryItem 
                key={`${entry.date}-${index}`}
                entry={entry}
                unit={unit}
                locale={locale}
                isLatest={isFirstPurchase}
              />
            )
          })
        )}
      </div>
    </div>
  )
}

export default SupplyPriceHistory
